import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Check, Lock } from "lucide-react";

import { Button } from "@qrody/ui";

import { api } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useTranslations } from "@/i18n/IntlProvider";

/**
 * /:locale/billing — full port of web/src/components/app/billing-panel.tsx.
 *
 * Surfaces the current plan + quotas, two pricing-cards (quarterly /
 * yearly), and the two write-paths into Stripe:
 *
 *   - `POST /v1/billing/checkout` — first-time purchase. Mints a Stripe-
 *     hosted Checkout URL; the browser hops via `location.assign`.
 *   - `POST /v1/billing/upgrade`  — paid → paid mutation. Stripe applies
 *     proration synchronously; we invalidate the plan query so the new
 *     tier shows up on next render.
 *   - `POST /v1/billing/portal`   — Stripe Customer Portal for paid
 *     users (manage payment method, cancel subscription, download
 *     invoices).
 *
 * Plan-relation logic mirrors the legacy `planRelation` helper:
 *   - tier1_free → any paid     = "upgrade" (label → "Auswählen")
 *   - paid       → same         = "current"
 *   - paid       → other-paid   = "upgrade" or "downgrade" by rank
 *   - yearly     → quarterly    = "downgrade" (blocked)
 */
type QuotaView = {
  feature: string;
  limit: number | null;
  used: number | null;
  remaining: number | null;
};

type AccountPlanResponse = {
  planId: string;
  toggles: Record<string, boolean>;
  quotas: ReadonlyArray<QuotaView>;
  enums: Record<string, string>;
};

type CheckoutResponse = {
  sessionId: string;
  url: string;
};

type PortalResponse = {
  url: string;
};

type UpgradeResponse = {
  previousPlan: string;
  newPlan: string;
};

type BillingPlanId = "tier1_free" | "pro_quarterly" | "pro_yearly";
type PlanRelation = "current" | "upgrade" | "downgrade";

const QUOTA_KEY_CODES = "qr.dynamic.max_active";
const QUOTA_KEY_SCAN_RETENTION = "analytics.retention_days";

/** Ascending rank — yearly outranks quarterly, both outrank free. */
const PLAN_RANK: Record<BillingPlanId, number> = {
  tier1_free: 0,
  pro_quarterly: 1,
  pro_yearly: 2,
};

function planRelation(current: string, target: BillingPlanId): PlanRelation {
  if (current === target) return "current";
  const cur = PLAN_RANK[current as BillingPlanId] ?? 0;
  const tgt = PLAN_RANK[target];
  return tgt > cur ? "upgrade" : "downgrade";
}

export function BillingRoute() {
  const t = useTranslations();
  const { locale } = useParams({ strict: false }) as { locale?: string };
  const lang = locale ?? "de";
  const qc = useQueryClient();

  const planQuery = useQuery({
    queryKey: ["billing-plan"],
    queryFn: () => api<AccountPlanResponse>("/v1/billing/plan"),
  });

  const plan = planQuery.data ?? null;
  const currentPlanId = plan?.planId ?? "tier1_free";
  const isOnPaidPlan = currentPlanId !== "tier1_free";

  // Two paths land on the same UI button — checkout for free → first
  // paid plan, upgrade for paid → paid. The backend enforces both rules
  // (409 on /checkout if already paid, 409 on /upgrade for downgrades).
  const planMut = useMutation<
    { kind: "checkout" | "upgrade"; targetPlan: BillingPlanId },
    Error,
    BillingPlanId
  >({
    mutationFn: async (planId) => {
      if (isOnPaidPlan) {
        await api<UpgradeResponse>("/v1/billing/upgrade", {
          method: "POST",
          body: JSON.stringify({ planId }),
        });
        return { kind: "upgrade", targetPlan: planId };
      }
      const base = window.location.origin;
      const res = await api<CheckoutResponse>("/v1/billing/checkout", {
        method: "POST",
        body: JSON.stringify({
          planId,
          successUrl: `${base}/${lang}/billing?upgrade=success`,
          cancelUrl: `${base}/${lang}/billing?upgrade=cancelled`,
        }),
      });
      // Stripe-hosted Checkout owns the success/cancel redirect.
      window.location.assign(res.url);
      return { kind: "checkout", targetPlan: planId };
    },
    onSuccess: (outcome) => {
      if (outcome.kind === "upgrade") {
        void qc.invalidateQueries({ queryKey: ["billing-plan"] });
      }
    },
  });

  const portalMut = useMutation<PortalResponse, Error>({
    mutationFn: async () => {
      const base = window.location.origin;
      return api<PortalResponse>("/v1/billing/portal", {
        method: "POST",
        body: JSON.stringify({ returnUrl: `${base}/${lang}/billing` }),
      });
    },
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });

  const byFeature = new Map((plan?.quotas ?? []).map((q) => [q.feature, q]));
  const codesQuota = byFeature.get(QUOTA_KEY_CODES);
  const scansQuota = byFeature.get(QUOTA_KEY_SCAN_RETENTION);

  const features = [
    t("app.billing.plans.feature.dynamic"),
    t("app.billing.plans.feature.scans"),
    t("app.billing.plans.feature.watermark"),
    t("app.billing.plans.feature.dpi"),
    t("app.billing.plans.feature.logo"),
  ];

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("app.billing.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("app.billing.subtitle")}
        </p>
      </header>

      {planQuery.isLoading && (
        <Card>
          <CardContent className="text-sm text-muted-foreground">
            {t("app.billing.loading")} …
          </CardContent>
        </Card>
      )}

      {planQuery.isError && (
        <Card>
          <CardContent className="text-sm text-destructive">
            {t("common.error")}: {(planQuery.error as Error).message}
          </CardContent>
        </Card>
      )}

      {plan && (
        <Card>
          <CardHeader>
            <CardTitle>{t("app.billing.plan.label")}</CardTitle>
            <CardDescription>{formatPlanId(currentPlanId, t)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <QuotaBar label={t("app.billing.quota.codes")} quota={codesQuota} />
            <QuotaBar
              label={t("app.billing.quota.scans")}
              quota={scansQuota}
              suffix=" d"
            />
          </CardContent>
        </Card>
      )}

      {/* Pricing-Cards — quarterly (left) + yearly (highlighted, right) */}
      <div className="grid gap-4 sm:grid-cols-2">
        <PlanCard
          label={t("app.billing.plans.quarterly.title")}
          price="30 €"
          cycle={t("app.billing.plans.quarterly.cycle")}
          perMonth={t("app.billing.plans.quarterly.perMonth", { value: "10" })}
          features={features}
          relation={planRelation(currentPlanId, "pro_quarterly")}
          isHighlighted={false}
          ctaLabel={
            isOnPaidPlan
              ? t("app.billing.upgradeCta")
              : t("app.billing.checkoutCta")
          }
          ctaPending={t("app.billing.upgradeInFlight")}
          downgradeHint={t("app.billing.downgradeHint")}
          onSelect={() => planMut.mutate("pro_quarterly")}
          isPending={
            planMut.isPending && planMut.variables === "pro_quarterly"
          }
        />
        <PlanCard
          label={t("app.billing.plans.yearly.title")}
          price="100 €"
          cycle={t("app.billing.plans.yearly.cycle")}
          perMonth={t("app.billing.plans.yearly.perMonth", { value: "8.33" })}
          badge={t("app.billing.plans.yearly.badge")}
          features={features}
          relation={planRelation(currentPlanId, "pro_yearly")}
          isHighlighted
          ctaLabel={
            isOnPaidPlan
              ? t("app.billing.upgradeCta")
              : t("app.billing.checkoutCta")
          }
          ctaPending={t("app.billing.upgradeInFlight")}
          downgradeHint={t("app.billing.downgradeHint")}
          onSelect={() => planMut.mutate("pro_yearly")}
          isPending={planMut.isPending && planMut.variables === "pro_yearly"}
        />
      </div>

      {planMut.isError ? (
        <p className="text-sm text-destructive">
          {(planMut.error as Error).message}
        </p>
      ) : null}
      {planMut.isSuccess && planMut.data?.kind === "upgrade" ? (
        <p
          role="status"
          aria-live="polite"
          className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
        >
          {t("app.billing.upgradeSuccess", {
            plan: formatPlanId(planMut.data.targetPlan, t),
          })}
        </p>
      ) : null}

      <Card>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={() => portalMut.mutate()}
              disabled={portalMut.isPending || !isOnPaidPlan}
              title={
                !isOnPaidPlan ? t("app.billing.portalNoSubHint") : undefined
              }
            >
              {portalMut.isPending
                ? `${t("app.billing.portalInFlight")} …`
                : t("app.billing.portalCta")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {isOnPaidPlan
                ? t("app.billing.portalHint")
                : t("app.billing.portalNoSubHint")}
            </span>
          </div>
          {portalMut.isError ? (
            <p className="text-sm text-destructive">
              {(portalMut.error as Error).message}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

function PlanCard({
  label,
  price,
  cycle,
  perMonth,
  badge,
  features,
  relation,
  isHighlighted,
  ctaLabel,
  ctaPending,
  downgradeHint,
  onSelect,
  isPending,
}: {
  label: string;
  price: string;
  cycle: string;
  perMonth: string;
  badge?: string;
  features: string[];
  relation: PlanRelation;
  isHighlighted: boolean;
  ctaLabel: string;
  ctaPending: string;
  downgradeHint: string;
  onSelect: () => void;
  isPending: boolean;
}) {
  const isCurrent = relation === "current";
  const isDowngrade = relation === "downgrade";

  return (
    <article
      className={
        "relative flex flex-col rounded-lg border p-5 " +
        (isHighlighted && !isDowngrade ? "border-primary shadow-sm " : "") +
        (isCurrent
          ? "border-emerald-300 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/20"
          : "")
      }
    >
      {badge && !isDowngrade ? (
        <span className="absolute -top-2 right-4 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
          {badge}
        </span>
      ) : null}
      <h3 className="text-lg font-semibold">{label}</h3>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-bold">{price}</span>
        <span className="text-sm text-muted-foreground">/ {cycle}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{perMonth}</p>

      <ul className="mt-4 flex-1 space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check
              className="mt-0.5 size-4 shrink-0 text-primary"
              aria-hidden
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <Button className="mt-5" variant="outline" disabled>
          ✓ Aktiv
        </Button>
      ) : isDowngrade ? (
        <Button
          className="mt-5"
          variant="outline"
          disabled
          title={downgradeHint}
          aria-label={downgradeHint}
        >
          <Lock className="mr-1.5 size-3.5" aria-hidden />
          {downgradeHint}
        </Button>
      ) : (
        <Button
          className="mt-5"
          variant={isHighlighted ? "default" : "outline"}
          onClick={onSelect}
          disabled={isPending}
        >
          {isPending ? `${ctaPending} …` : ctaLabel}
        </Button>
      )}
    </article>
  );
}

function QuotaBar({
  label,
  quota,
  suffix,
}: {
  label: string;
  quota?: QuotaView;
  suffix?: string;
}) {
  if (!quota) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium text-muted-foreground">—</span>
        </div>
      </div>
    );
  }

  const limit = quota.limit;
  const used = quota.used ?? 0;
  const isUnlimited = limit === null;
  const ratio =
    !isUnlimited && limit && limit > 0 ? Math.min(used / limit, 1) : 0;
  const percent = Math.round(ratio * 100);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {isUnlimited ? "∞" : `${used} / ${limit}${suffix ?? ""}`}
        </span>
      </div>
      {!isUnlimited && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={label}
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

function formatPlanId(
  planId: string,
  t: (key: string, vars?: Record<string, string>) => string,
): string {
  if (planId === "tier1_free") return t("app.billing.plan.freePlan");
  if (planId === "pro_quarterly") return t("app.billing.plan.proQuarterly");
  if (planId === "pro_yearly") return t("app.billing.plan.proYearly");
  return planId;
}
